import React from "react";
import { Box, Paper, Typography, Skeleton, Chip } from "@mui/material";
import { Timer24Regular, Warning12Regular, Sparkle24Regular, TextBulletListCheckmark20Regular, ClipboardTaskListRtl24Regular, History24Regular } from "@fluentui/react-icons";
import { THEME, useOverviewData, getTimeRangeLabel } from "./OverviewContext";
import { CTLInfoTooltip } from "../utils/CTLInfoTooltip";

const TopSection: React.FC = () => {
  const { data, isLoading, dateRange, customDateRange } = useOverviewData();
  const timeRangeLabel = getTimeRangeLabel(dateRange, customDateRange);

  const displayTotal = data?.summary.totalCustomerRequests ?? 0;
  const inProgressCount = data?.summary.inTriageProcessCount ?? 0;

  if (isLoading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={150} sx={{ borderRadius: "12px" }} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        gap: 2,
        alignItems: "stretch"
      }}
    >
      {/* Total Customer Requests */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          backgroundColor: "#F7F9FC"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <TextBulletListCheckmark20Regular style={{ color: THEME.primary }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: THEME.textPrimary }}>TOTAL CUSTOMER REQUESTS</Typography>
          <CTLInfoTooltip
            title="Total Customer Requests"
            description="The overall size of the customer demand we are tracking — every feature request a customer has raised with us that is captured in Close the Loop."
            howItsCalculated="We count every customer request record, then apply whatever product and time-period filters you have chosen at the top of the page."
            note="Changing the date range or product filter will change this number."
          />
          <Chip
            icon={<History24Regular style={{ fontSize: 12 }} />}
            label={timeRangeLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
              color: dateRange === "2years" ? THEME.primary : "#996800",
              "& .MuiChip-icon": { color: "inherit", marginLeft: "4px" }
            }}
          />
        </Box>
        <Typography sx={{ fontSize: "30px", lineHeight: 1.15, fontWeight: 700, color: THEME.textPrimary }}>
          {displayTotal.toLocaleString()}
        </Typography>
      </Paper>

      {/* In Progress */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          backgroundColor: "#F7F9FC"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <ClipboardTaskListRtl24Regular style={{ color: THEME.warning }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: THEME.textPrimary }}>
            # OF CUSTOMER REQUESTS 'IN PROGRESS' STATE
          </Typography>
          <CTLInfoTooltip
            title="Customer Requests In Progress"
            description="How much of the customer demand is actively being worked on right now, rather than waiting or already closed out. A high number here means a lot of work is open at once."
            howItsCalculated="We count the requests whose customer-facing status currently reads 'In Progress', within the product and time period you have selected."
            note="This is a live snapshot of where requests sit today, not a historical total."
          />
          <Chip
            icon={<History24Regular style={{ fontSize: 12 }} />}
            label={timeRangeLabel}
            size="small"
            sx={{
              height: 20,
              fontSize: "10px",
              fontWeight: 600,
              backgroundColor: dateRange === "2years" ? "#E7F1FF" : "#FFF4CE",
              color: dateRange === "2years" ? THEME.primary : "#996800",
              "& .MuiChip-icon": { color: "inherit", marginLeft: "4px" }
            }}
          />
        </Box>
        <Typography sx={{ fontSize: "30px", lineHeight: 1.15, fontWeight: 700, color: THEME.textPrimary }}>
          {inProgressCount.toLocaleString()}
        </Typography>
      </Paper>

      {/* Time to First Disposition */}
      <Paper
        elevation={0}
        sx={{
          p: 0,
          borderRadius: "12px",
          border: `1px solid ${THEME.border}`,
          backgroundColor: THEME.surface,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "100%"
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "stretch",
            alignItems: "stretch",
            p: 0,
            overflow: "hidden",
            backgroundColor: "#F8FAFC"
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FAFBFC",
              py: 1.25,
              px: 1,
              gap: 0.75
            }}
          >
            <Timer24Regular style={{ color: THEME.primary }} />
            <Typography variant="body2" sx={{ color: THEME.primary, fontWeight: 700 }}>
              CUSTOMER REQUEST TIME TO FIRST DISPOSITION
            </Typography>
            <CTLInfoTooltip
              title="Time to First Disposition"
              description="How long a customer typically waits before we give them a first answer on their request. It highlights the product that keeps customers waiting the longest (Biggest Gap) and the one that responds quickest (Fastest)."
              howItsCalculated="For each product we take the average number of days between a request being raised and it reaching its first decision. The slowest product is shown as Biggest Gap and the quickest as Fastest."
              note="Only products with at least 5 requests are compared, so a single slow request cannot skew the result."
              color="#5A5A72"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0, p: 0, width: "100%", flex: 1, minHeight: 96 }}>
            {/* Biggest Gap */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                backgroundColor: "#FDF6F7"
              }}
            >
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 0.25 }}>
                  <Warning12Regular style={{ color: THEME.error, width: 20, height: 20 }} />
                  <Typography variant="caption" sx={{ color: THEME.error, fontWeight: 700 }}>
                    BIGGEST GAP
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "black", fontWeight: 700 }}>
                  {data?.biggestGap?.productName || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.error, fontWeight: 700 }}>
                  {data?.biggestGap ? `${Math.round(data.biggestGap.avgEndToEndDays)}d avg` : "—"}
                </Typography>
              </Box>
            </Box>

            {/* Fastest */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                backgroundColor: "#F4FBF4"
              }}
            >
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mb: 0.25 }}>
                  <Sparkle24Regular style={{ color: THEME.success, width: 24, height: 24 }} />
                  <Typography variant="caption" sx={{ color: THEME.success, fontWeight: 700 }}>
                    FASTEST
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: "black", fontWeight: 700 }}>
                  {data?.fastest?.productName || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.success, fontWeight: 700 }}>
                  {data?.fastest ? `${Math.round(data.fastest.avgEndToEndDays)}d avg` : "—"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TopSection;
